import { useState, useEffect, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency, SEAT_PRICE, isPaidRole } from "@/lib/billing-utils";
import { getInitials } from "@/lib/avatar-utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { AppDataTable } from "@/components/ui/app-data-table";

interface TeamMember {
  id: string;
  name: string;
  email: string;
  currentRoles: string[];
  status: 'invited' | 'onboarded';
}

interface SeatManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  teamMembers: TeamMember[];
  onSeatChange?: () => void;
}

export function SeatManagementDialog({ 
  isOpen, 
  onClose, 
  teamMembers, 
  onSeatChange 
}: SeatManagementDialogProps) {
  const { toast } = useToast();
  const { profile } = useAuth();
  const [saving, setSaving] = useState(false);
  
  // Initialize member roles from props
  const getInitialRoles = () => {
    const roles: Record<string, string[]> = {};
    teamMembers.forEach(member => {
      roles[member.id] = [...member.currentRoles];
    });
    return roles;
  };
  
  const [memberRoles, setMemberRoles] = useState<Record<string, string[]>>(getInitialRoles);

  // Update roles when teamMembers prop changes
  useEffect(() => {
    setMemberRoles(getInitialRoles());
  }, [teamMembers]);

  const handleRoleChange = (memberId: string, role: string) => {
    setMemberRoles(prev => {
      const currentRoles = prev[memberId] || [];
      const hasRole = currentRoles.includes(role);

      if (hasRole) {
        // Remove role
        return {
          ...prev,
          [memberId]: currentRoles.filter(r => r !== role)
        };
      } else {
        // Add role (can have multiple roles)
        return {
          ...prev,
          [memberId]: [...currentRoles, role]
        };
      }
    });
  };

  const getRoleBadge = (roles: string[]) => {
    if (roles.length === 0) return 'No Role';
    if (roles.length === 1) return roles[0].charAt(0).toUpperCase() + roles[0].slice(1);
    return `${roles.length} Roles`;
  };

  const countPaidSeats = () => {
    // Count unique users who have at least one paid role (billing is per user, not per role)
    let count = 0;
    Object.values(memberRoles).forEach(roles => {
      // If user has any paid role, count them once
      if (roles.some(role => isPaidRole(role))) {
        count++;
      }
    });
    return count;
  };

  const countFreeSeats = () => {
    let count = 0;
    Object.values(memberRoles).forEach(roles => {
      if (roles.length === 0 || roles.every(role => role === 'viewer')) {
        count++;
      }
    });
    return count;
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      // Update roles for each member
      for (const member of teamMembers) {
        const newRoles = memberRoles[member.id] || [];
        const currentRoles = member.currentRoles;
        
        const rolesToAdd = newRoles.filter(r => !currentRoles.includes(r));
        const rolesToRemove = currentRoles.filter(r => !newRoles.includes(r));

        // Add new roles
        for (const role of rolesToAdd) {
          const { error } = await supabase
            .from('user_roles')
            .insert({ 
              user_id: member.id, 
              role: role as any,
              company_id: profile?.company_id
            });
          
          if (error) throw error;
        }

        // Remove old roles
        for (const role of rolesToRemove) {
          const { error } = await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', member.id)
            .eq('role', role as any);
          
          if (error) throw error;
        }
      }

      toast({
        title: "Success",
        description: "Team member roles updated successfully."
      });

      onSeatChange?.();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update roles: " + (error.message || "Unknown error"),
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const paidSeats = countPaidSeats();
  const freeSeats = countFreeSeats();

  const memberColumns = useMemo<ColumnDef<TeamMember>[]>(() => [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback>{getInitials(row.original.name)}</AvatarFallback>
          </Avatar>
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.email}</span>,
    },
    {
      id: "currentRole",
      header: "Current Role",
      cell: ({ row }) => (
        <Badge variant="outline">
          {getRoleBadge(memberRoles[row.original.id] || [])}
        </Badge>
      ),
      enableSorting: false,
    },
    {
      id: "changeRole",
      header: "Change Role",
      cell: ({ row }) => (
        <div className="flex gap-2">
          {["viewer", "creator", "reviewer", "approver"].map((role) => (
            <Button
              key={role}
              variant={memberRoles[row.original.id]?.includes(role) ? "default" : "outline"}
              size="sm"
              onClick={() => handleRoleChange(row.original.id, role)}
              disabled={row.original.status === "invited"}
            >
              {role.charAt(0).toUpperCase() + role.slice(1)}
            </Button>
          ))}
        </div>
      ),
      enableSorting: false,
    },
  ], [memberRoles]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Team Users</DialogTitle>
          <DialogDescription>
            Assign roles to team members. Users with Creator, Reviewer, or Approver roles are charged per user (not per role).
          </DialogDescription>
          <div className="flex gap-2 mt-2">
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              {paidSeats} Paid Users
            </Badge>
            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
              {freeSeats} Free Viewers
            </Badge>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <AppDataTable
            columns={memberColumns}
            data={teamMembers}
            emptyMessage="No team members available."
            getRowId={(member) => member.id}
          />
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm font-semibold">
              Total Cost: {formatCurrency(paidSeats * SEAT_PRICE)}/month
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
